import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
// framer-motion removed for React 19 compatibility - using CSS transitions instead
import { Camera, Download, Search, Sparkles, User, Calendar, Image as ImageIcon, X, Heart, ChevronDown, Upload, QrCode, ChevronLeft, ChevronRight, Share2, ThumbsUp, Lock, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import WebcamCapture from '../components/WebcamCapture';
import { FavoritesListModal } from '../components/FavoritesListModal';
import GalleryAccessGate from '../components/GalleryAccessGate';
import DownloadSizeDialog from '../components/DownloadSizeDialog';
import ThemeToggle from '../components/ThemeToggle';
import QRCodeModal from '../components/QRCodeModal';
import PhotoGridSkeleton from '../components/PhotoGridSkeleton';
import MobileBottomNav from '../components/MobileBottomNav';
// ViewToggle removed - defaulting to masonry view
import MasonryGrid from '../components/MasonryGrid';
import SharePhotoModal from '../components/SharePhotoModal';
import PhotoLikeButton from '../components/PhotoLikeButton';
import ShareCollectionCard from '../components/ShareCollectionCard';
import { useSwipeGesture } from '../hooks/useSwipeGesture';
import { getVisitorId, trackVisit, trackIdentify, trackSave, visitorQuery, appendVisitorFields } from '../lib/visitorTracking';
import useSavePhoto, { filenameFromResponse } from '../hooks/useSavePhoto';
import PhotoLightbox from '../components/gallery/PhotoLightbox';
import CinemaHero from '../components/gallery/cinema/CinemaHero';
import CinemaFooter from '../components/gallery/cinema/CinemaFooter';
import FindMyPhotosBar from '../components/gallery/cinema/FindMyPhotosBar';
import CollectionPills from '../components/gallery/cinema/CollectionPills';
import FaceSearchProgress from '../components/search/FaceSearchProgress';
import { TagSearchBar } from '../components/search/TagSearchBar';
import { TagCloud } from '../components/search/TagCloud';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const PublicGallery = () => {
  const { shareToken, collectionSlug } = useParams();
  const navigate = useNavigate();
  const [gallery, setGallery] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedCollectionLink, setCopiedCollectionLink] = useState(false);
  
  // Access gate state
  const [hasAccess, setHasAccess] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [categorizedTags, setCategorizedTags] = useState({
    people_emotions: [],
    business_networking: [],
    presentations: [],
    booths_exhibition: [],
    equipment_media: [],
    other: []
  });
  // Removed searchMode - Browse by Category is the only mode
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [faceSearchFile, setFaceSearchFile] = useState(null);
  const [faceSearching, setFaceSearching] = useState(false);
  const [faceSearchResults, setFaceSearchResults] = useState([]);
  const [faceSearchAccessToken, setFaceSearchAccessToken] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(collectionSlug || null); // null = "All"
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  // When a ?photo=ID deep-link targets a photo not in the loaded page, we show it
  // standalone in the lightbox.
  const [deepLinkPhoto, setDeepLinkPhoto] = useState(null);
  const [showWebcam, setShowWebcam] = useState(false);
  const [showUserInfoDialog, setShowUserInfoDialog] = useState(false);
  const [userName, setUserName] = useState('');
  const { savePhoto, savePrompt } = useSavePhoto();
  const [userEmail, setUserEmail] = useState('');
  const [pendingDownloadPhoto, setPendingDownloadPhoto] = useState(null);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [downloadRequestSuccess, setDownloadRequestSuccess] = useState(false);
  const [downloadUserEmailNotify, setDownloadUserEmailNotify] = useState('');
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest' or 'oldest'
  const [aiTop30Enabled, setAiTop30Enabled] = useState(false);
  const [showAITop30Only, setShowAITop30Only] = useState(false);
  
  // Pagination state for infinite scroll
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const PHOTOS_PER_PAGE = 100;
  
  // Download size selection
  const [showDownloadSizeDialog, setShowDownloadSizeDialog] = useState(false);
  const [pendingDownloadAction, setPendingDownloadAction] = useState(null); // { type: 'single' | 'all', photo: Photo | null }
  const [isPreparingDownload, setIsPreparingDownload] = useState(false);
  
  // Paywall state
  const [showPaywallModal, setShowPaywallModal] = useState(false);

  // Favorites state
  const [showFavoritesModal, setShowFavoritesModal] = useState(false);
  const [pendingFavoritePhoto, setPendingFavoritePhoto] = useState(null);
  const [userFavoritesLists, setUserFavoritesLists] = useState([]);
  const [favoritesEmail, setFavoritesEmail] = useState('');
  const [showFavoritesEmailDialog, setShowFavoritesEmailDialog] = useState(false);
  const [selectedFavoritesList, setSelectedFavoritesList] = useState(null);
  const [favoritesPhotos, setFavoritesPhotos] = useState([]);
  const [favoritedPhotoIds, setFavoritedPhotoIds] = useState(new Set());
  const [showCreateListDialog, setShowCreateListDialog] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [listToDelete, setListToDelete] = useState(null);
  
  // Find My Photos Modal State
  const [showFindMyPhotosModal, setShowFindMyPhotosModal] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyConsent, setNotifyConsent] = useState(false);
  const [submittingSubscription, setSubmittingSubscription] = useState(false);

  // UI Enhancement State
  const [showQRModal, setShowQRModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharePhoto, setSharePhoto] = useState(null);
  // Header visibility on scroll (only show after scrolling past hero)
  const [showHeader, setShowHeader] = useState(false);

  // Swipe gesture handlers for lightbox
  const swipeHandlers = useSwipeGesture({
    onSwipeLeft: () => lightboxPhoto && navigateLightbox('next'),
    onSwipeRight: () => lightboxPhoto && navigateLightbox('prev'),
    onSwipeDown: () => setLightboxPhoto(null),
    threshold: 50
  });

  // Show header after scrolling past hero (100vh)
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const heroHeight = window.innerHeight; // 100vh
      setShowHeader(scrollY > heroHeight - 100); // Show header slightly before hero ends
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial scroll position

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Check if user has already accessed this gallery
    const storedUserInfo = localStorage.getItem('gallery_user_info');
    if (storedUserInfo) {
      const parsed = JSON.parse(storedUserInfo);
      setUserInfo(parsed);
      setHasAccess(true);
      setUserName(parsed.name);
      setUserEmail(parsed.email);
      setFavoritesEmail(parsed.email);
      setNotifyEmail(parsed.email);
      // Details entered on an earlier visit still identify this browser here.
      trackIdentify(`${BACKEND_URL}/api/public/gallery/${shareToken}/track`, parsed);
    }
    
    fetchGalleryData(null, collectionSlug || null);
    trackVisit(`${BACKEND_URL}/api/public/gallery/${shareToken}/track`, { collection: collectionSlug || null });

    // Check for subscription parameter (from email link)
    const urlParams = new URLSearchParams(window.location.search);
    const subscriptionToken = urlParams.get('subscription');
    if (subscriptionToken) {
      loadSubscriptionMatches(subscriptionToken);
    } else {
      // Try to load saved access token from previous face search
      const savedAccessToken = sessionStorage.getItem(`faceSearchToken_${shareToken}`);
      if (savedAccessToken) {
        setFaceSearchAccessToken(savedAccessToken);
        loadSubscriptionMatches(savedAccessToken);
      }
    }
  }, [shareToken]);

  const handleAccessGranted = (info) => {
    setUserInfo(info);
    setHasAccess(true);
    setUserName(info.name);
    setUserEmail(info.email);
    setFavoritesEmail(info.email);
    setNotifyEmail(info.email);
    trackIdentify(`${BACKEND_URL}/api/public/gallery/${shareToken}/track`, info);
    toast.success(`Welcome, ${info.name}!`);
  };

  // Handle collection change - refetch photos filtered by collection and sync URL
  const handleCollectionChange = async (newCollectionSlug, options = {}) => {
    const { skipNavigate = false, skipScroll = false } = options;
    setSelectedCollection(newCollectionSlug);

    if (!skipNavigate) {
      const targetPath = newCollectionSlug
        ? `/share/${shareToken}/c/${newCollectionSlug}`
        : `/share/${shareToken}`;
      navigate(targetPath, { replace: false });
    }

    try {
      const collParam = newCollectionSlug ? `&collection=${newCollectionSlug}` : '';
      const response = await axios.get(
        `${BACKEND_URL}/api/public/gallery/${shareToken}/photos?limit=${PHOTOS_PER_PAGE}&skip=0&sort=${sortOrder}${collParam}${showAITop30Only ? '&ai_top30=true' : ''}`
      );
      const photosData = Array.isArray(response.data) ? response.data : [];
      setPhotos(photosData);
      setPage(0);
      setHasMore(!showAITop30Only && photosData.length === PHOTOS_PER_PAGE);

      if (!skipScroll) {
        window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error filtering by collection:', error);
      toast.error('Failed to filter photos');
    }
  };

  // React to browser back/forward (URL collection slug changing without a pill click)
  useEffect(() => {
    const urlSlug = collectionSlug || null;
    if (urlSlug !== selectedCollection) {
      handleCollectionChange(urlSlug, { skipNavigate: true, skipScroll: true });
    }
  }, [collectionSlug]);

  const copyCollectionLink = async (slug) => {
    const url = `${window.location.origin}/share/${shareToken}/c/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCollectionLink(true);
      toast.success('Collection link copied');
      setTimeout(() => setCopiedCollectionLink(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  // Handle sort order change - refetch photos with new sort order
  const handleSortChange = async (newSortOrder) => {
    if (newSortOrder === sortOrder) return;

    setSortOrder(newSortOrder);

    try {
      const collParam = selectedCollection ? `&collection=${selectedCollection}` : '';
      // Reset pagination and fetch fresh with new sort order
      const response = await axios.get(
        `${BACKEND_URL}/api/public/gallery/${shareToken}/photos?limit=${PHOTOS_PER_PAGE}&skip=0&sort=${newSortOrder}${collParam}${showAITop30Only ? '&ai_top30=true' : ''}`
      );
      const photosData = Array.isArray(response.data) ? response.data : [];
      setPhotos(photosData);
      setPage(0);
      setHasMore(!showAITop30Only && photosData.length === PHOTOS_PER_PAGE);
    } catch (error) {
      console.error('Error changing sort order:', error);
      toast.error('Failed to change sort order');
    }
  };

  const fetchGalleryData = async (currentSortOrder = null, initialCollectionSlug = null) => {
    try {
      setLoading(true);

      // Fetch gallery meta first so we can honour the gallery's configured default sort.
      const galleryRes = await axios.get(`${BACKEND_URL}/api/public/gallery/${shareToken}`);
      setGallery(galleryRes.data);
      setCollections(galleryRes.data.collections || []);
      setAiTop30Enabled(galleryRes.data.ai_top30_enabled || false);

      // Resolve sort: explicit override (e.g. a user change) wins, else the gallery default.
      const resolvedSort = currentSortOrder || galleryRes.data.default_sort || 'newest';
      setSortOrder(resolvedSort);

      const collParam = initialCollectionSlug ? `&collection=${initialCollectionSlug}` : '';
      const [photosRes, tagsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/public/gallery/${shareToken}/photos?limit=${PHOTOS_PER_PAGE}&skip=0&sort=${resolvedSort}${collParam}`),
        axios.get(`${BACKEND_URL}/api/public/gallery/${shareToken}/tags`)
      ]);

      const photosData = Array.isArray(photosRes.data) ? photosRes.data : [];
      setPhotos(photosData);

      // Check if we have more photos to load
      setHasMore(photosData.length === PHOTOS_PER_PAGE);
      setPage(0);

      // Deep-link: ?photo=ID opens that photo in the lightbox.
      const photoParam = new URLSearchParams(window.location.search).get('photo');
      if (photoParam) {
        const idx = photosData.findIndex((p) => p.id === photoParam);
        if (idx >= 0) {
          setDeepLinkPhoto(null);
          setLightboxIndex(idx);
          setLightboxPhoto(photosData[idx]);
        } else {
          try {
            const single = await axios.get(`${BACKEND_URL}/api/public/gallery/${shareToken}/photo/${photoParam}`);
            setDeepLinkPhoto(single.data);
            setLightboxIndex(0);
            setLightboxPhoto(single.data);
          } catch (e) {
            // photo not found in this gallery — ignore the param
          }
        }
      }

      // Add null guards for nested data access
      const tagsData = tagsRes?.data || {};
      setAllTags(Array.isArray(tagsData.tags) ? tagsData.tags : []);
      setCategorizedTags(tagsData.categorized || {
        facial_attributes: [],
        emotions: [],
        activities: [],
        people_groups: [],
        event_scenes: [],
        event_objects: [],
        atmosphere: [],
        other: []
      });
    } catch (error) {
      console.error('Error fetching gallery:', error);
      toast.error('Gallery not found');
      // Ensure arrays are set even on error
      setPhotos([]);
      setAllTags([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMorePhotos = async (currentSortOrder = sortOrder) => {
    if (loadingMore || !hasMore || showAITop30Only) return;

    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const skip = nextPage * PHOTOS_PER_PAGE;
      const collParam = selectedCollection ? `&collection=${selectedCollection}` : '';

      const response = await axios.get(
        `${BACKEND_URL}/api/public/gallery/${shareToken}/photos?limit=${PHOTOS_PER_PAGE}&skip=${skip}&sort=${currentSortOrder}${collParam}`
      );

      const newPhotos = Array.isArray(response.data) ? response.data : [];
      setPhotos(prev => [...prev, ...newPhotos]);
      setPage(nextPage);
      setHasMore(newPhotos.length === PHOTOS_PER_PAGE);
    } catch (error) {
      console.error('Error loading more photos:', error);
      toast.error('Failed to load more photos');
    } finally {
      setLoadingMore(false);
    }
  };

  const toggleAITop30 = async () => {
    try {
      const newState = !showAITop30Only;
      setShowAITop30Only(newState);
      const collParam = selectedCollection ? `&collection=${selectedCollection}` : '';

      if (newState) {
        // Fetch AI Top 30 photos
        toast.info('Loading AI Top 30 photos...');
        const response = await axios.get(
          `${BACKEND_URL}/api/public/gallery/${shareToken}/photos?ai_top30=true&sort=${sortOrder}${collParam}`
        );
        setPhotos(Array.isArray(response.data) ? response.data : []);
        setHasMore(false); // AI Top 30 loads all at once
        toast.success('Showing AI Top 30 photos');
      } else {
        // Fetch first page of all photos
        const response = await axios.get(
          `${BACKEND_URL}/api/public/gallery/${shareToken}/photos?limit=${PHOTOS_PER_PAGE}&skip=0&sort=${sortOrder}${collParam}`
        );
        const photosData = Array.isArray(response.data) ? response.data : [];
        setPhotos(photosData);
        setPage(0);
        setHasMore(photosData.length === PHOTOS_PER_PAGE);
        toast.success('Showing all photos');
      }

      // Reset to all tab when toggling
      setActiveTab('all');
    } catch (error) {
      console.error('Error toggling AI Top 30:', error);
      toast.error('Failed to load photos');
    }
  };


  const loadSubscriptionMatches = async (accessToken) => {
    try {
      console.log('Loading subscription matches with token:', accessToken);
      
      const response = await axios.get(
        `${BACKEND_URL}/api/public/gallery/${shareToken}/my-photos/${accessToken}`
      );
      
      if (response.data.matches && response.data.matches.length > 0) {
        setFaceSearchResults(response.data.matches);
        setActiveTab('faces');
        toast.success(`Found ${response.data.total} photo(s) matching your face!`);
      } else {
        setActiveTab('faces');
        toast.info('No matching photos found at this time. New photos may be uploaded soon!');
      }
    } catch (error) {
      console.error('Error loading subscription matches:', error);
      toast.error('Unable to load your matched photos. Please try the face search manually.');
    }
  };


  // Load favorites lists when switching to favorites tab
  useEffect(() => {
    if (activeTab === 'favorites') {
      const storedEmail = sessionStorage.getItem('favoritesUserEmail');
      if (storedEmail) {
        setFavoritesEmail(storedEmail);
        loadUserFavoritesLists(storedEmail);
      }
    }
  }, [activeTab]);



  const handleSearchByTags = async (directQuery = null) => {
    const searchTerms = directQuery || tagSearchQuery.trim() || selectedTags.join(',');
    
    if (!searchTerms && selectedTags.length === 0) {
      setSearchResults([]);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('tags', searchTerms);
      
      // Add category filter if not 'all'
      if (selectedCategory !== 'all') {
        formData.append('category', selectedCategory);
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/public/gallery/${shareToken}/search/by-tags`,
        formData
      );

      setSearchResults(response.data.photos);
      setActiveTab('tags');
      
      // Show smart search feedback
      const expandedCount = response.data.expanded_terms?.length || 0;
      const originalCount = response.data.search_terms?.length || 0;
      if (expandedCount > originalCount) {
        toast.success(`Found ${response.data.total} photos (smart search expanded to ${expandedCount} related terms)`);
      } else {
        toast.success(`Found ${response.data.total} matching photos`);
      }
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const handleFaceSearch = async () => {
    if (!faceSearchFile) {
      toast.error('Please upload a reference photo or take a selfie');
      return;
    }

    setFaceSearching(true);
    try {
      const formData = new FormData();
      formData.append('file', faceSearchFile);
      
      // Add email if available for immediate notification
      const emailToUse = userEmail || favoritesEmail || sessionStorage.getItem('downloadUserEmail');
      if (emailToUse) {
        formData.append('email', emailToUse);
      }
      appendVisitorFields(formData, { name: userName }, { nameField: 'name' });

      const response = await axios.post(
        `${BACKEND_URL}/api/public/gallery/${shareToken}/search/faces`,
        formData
      );

      setFaceSearchResults(response.data.matches);
      
      // Save access token if provided (for persisting results across sessions)
      if (response.data.access_token) {
        setFaceSearchAccessToken(response.data.access_token);
        sessionStorage.setItem(`faceSearchToken_${shareToken}`, response.data.access_token);
      }
      
      if (response.data.total === 0) {
        // No matches found - show "Find My Photos" modal
        setShowFindMyPhotosModal(true);
      } else {
        // Matches found - switch to faces tab to show results
        setActiveTab('faces');
        toast.success(`Found ${response.data.total} photos with matching faces`);
      }
    } catch (error) {
      toast.error('Face search failed');
    } finally {
      setFaceSearching(false);
    }
  };

  const handleWebcamCapture = async (file) => {
    setFaceSearchFile(file);
    setShowWebcam(false);
    
    // Immediately trigger face search
    setFaceSearching(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Add email if available for immediate notification
      const emailToUse = userEmail || favoritesEmail || sessionStorage.getItem('downloadUserEmail');
      if (emailToUse) {
        formData.append('email', emailToUse);
      }
      appendVisitorFields(formData, { name: userName }, { nameField: 'name' });

      const response = await axios.post(
        `${BACKEND_URL}/api/public/gallery/${shareToken}/search/faces`,
        formData
      );

      setFaceSearchResults(response.data.matches);
      
      // Save access token if provided (for persisting results across sessions)
      if (response.data.access_token) {
        setFaceSearchAccessToken(response.data.access_token);
        sessionStorage.setItem(`faceSearchToken_${shareToken}`, response.data.access_token);
      }
      
      if (response.data.total === 0) {
        // No matches found - show "Find My Photos" modal
        setShowFindMyPhotosModal(true);
        toast.info('No matches found yet. Would you like to be notified when your photos are uploaded?');
      } else {
        // Matches found - switch to faces tab to show results
        setActiveTab('faces');
        toast.success(`Found ${response.data.total} photos with your face!`);
      }
    } catch (error) {
      toast.error('Face search failed. Please try again.');
    } finally {
      setFaceSearching(false);
    }
  };

  const handleSubscribeToNotifications = async () => {
    if (!notifyEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(notifyEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (!notifyConsent) {
      toast.error('Please consent to facial recognition to continue');
      return;
    }
    
    if (!faceSearchFile) {
      toast.error('Reference photo missing. Please try searching again.');
      return;
    }
    
    setSubmittingSubscription(true);
    try {
      const formData = new FormData();
      formData.append('file', faceSearchFile);
      formData.append('email', notifyEmail);
      formData.append('consent', notifyConsent);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/public/gallery/${shareToken}/face-subscription`,
        formData
      );
      
      toast.success(response.data.message);
      setShowFindMyPhotosModal(false);
      setNotifyEmail('');
      setNotifyConsent(false);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to create subscription';
      toast.error(errorMsg);
    } finally {
      setSubmittingSubscription(false);
    }
  };

  const handleDownloadPhoto = async (photo) => {
    // Paywall check
    if (gallery?.paywall_enabled) {
      setShowPaywallModal(true);
      return;
    }

    // Check if user info is already captured
    if (!userName || !userEmail) {
      // Store the photo to download and show dialog
      setPendingDownloadPhoto(photo);
      setShowUserInfoDialog(true);
      return;
    }

    // User info exists, show size selection dialog
    setPendingDownloadAction({ type: 'single', photo });
    setShowDownloadSizeDialog(true);
  };

  const handleUserInfoSubmit = async () => {
    if (!userName.trim() || !userEmail.trim()) {
      toast.error('Please enter both name and email');
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Store in session storage
    sessionStorage.setItem('downloadUserName', userName);
    sessionStorage.setItem('downloadUserEmail', userEmail);
    trackIdentify(`${BACKEND_URL}/api/public/gallery/${shareToken}/track`, { name: userName, email: userEmail });

    // Close dialog and proceed to size selection
    setShowUserInfoDialog(false);
    
    if (pendingDownloadPhoto === 'DOWNLOAD_ALL') {
      // Show size dialog for download all
      setPendingDownloadAction({ type: 'all', photo: null });
      setShowDownloadSizeDialog(true);
      setPendingDownloadPhoto(null);
    } else if (pendingDownloadPhoto) {
      // Show size dialog for single photo
      setPendingDownloadAction({ type: 'single', photo: pendingDownloadPhoto });
      setShowDownloadSizeDialog(true);
      setPendingDownloadPhoto(null);
    }
  };

  const handleDownloadSizeSelection = async (size) => {
    if (!pendingDownloadAction) return;
    
    // Show loading state
    setIsPreparingDownload(true);
    
    const { type, photo, list } = pendingDownloadAction;
    
    try {
      if (type === 'single') {
        await performDownload(photo, size);
      } else if (type === 'all') {
        const storedName = sessionStorage.getItem('downloadUserName');
        const storedEmail = sessionStorage.getItem('downloadUserEmail');
        await executeDownloadAll(storedName, storedEmail, size);
      } else if (type === 'favorites') {
        await executeFavoritesListDownload(list, size);
      }
    } finally {
      // Hide loading state after download starts
      setTimeout(() => {
        setIsPreparingDownload(false);
        setShowDownloadSizeDialog(false);
        setPendingDownloadAction(null);
      }, 1000); // Small delay to ensure download has started
    }
  };

  const performDownload = async (photo, size = 'full') => {
    try {
      // Download the photo with size parameter
      const response = await axios.get(
        `${BACKEND_URL}/api/public/gallery/${shareToken}/photo/${photo.id}/download?size=${size}&${visitorQuery({ name: userName, email: userEmail })}`,
        { responseType: 'blob' }
      );

      // Not awaited: the size dialog must close before a "Save to Photos" prompt can take a tap.
      savePhoto(response.data, filenameFromResponse(response, photo.filename)).then((outcome) => {
        trackSave(`${BACKEND_URL}/api/public/gallery/${shareToken}/track`, {
          name: userName, email: userEmail, photoId: photo.id, outcome,
        });
        if (outcome === 'downloaded') toast.success('Download started!');
      });
    } catch (error) {
      toast.error('Download failed');
      console.error('Download error:', error);
    }
  };

  // Favorites functions
  const handleFavoriteClick = async (photo) => {
    setPendingFavoritePhoto(photo);
    
    // Check if we have cached lists
    if (userFavoritesLists.length > 0) {
      setShowFavoritesModal(true);
    } else {
      // Need to get user's email first
      const storedEmail = sessionStorage.getItem('favoritesUserEmail');
      if (storedEmail) {
        setFavoritesEmail(storedEmail);
        await loadUserFavoritesLists(storedEmail);
        setShowFavoritesModal(true); // Open modal after loading lists
      } else {
        setShowFavoritesModal(true); // Will show create new list form
      }
    }
  };

  const loadUserFavoritesLists = async (email) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/public/gallery/${shareToken}/favorites/lists/${encodeURIComponent(email)}`
      );
      setUserFavoritesLists(response.data);
      // Don't open modal - only open when user clicks heart on photo
    } catch (error) {
      console.error('Error loading favorites lists:', error);
      // Don't show modal on error either
    }
  };

  const handleCreateFavoritesList = async (listName, email, photoId) => {
    try {
      // Create the list
      const listResponse = await axios.post(
        `${BACKEND_URL}/api/public/gallery/${shareToken}/favorites/lists`,
        {
          list_name: listName,
          user_email: email
        }
      );

      // Add the photo to the list
      await axios.post(
        `${BACKEND_URL}/api/public/gallery/${shareToken}/favorites/add`,
        {
          list_id: listResponse.data.id,
          photo_id: photoId
        }
      );

      // Cache email and reload lists
      sessionStorage.setItem('favoritesUserEmail', email);
      setFavoritesEmail(email);
      await loadUserFavoritesLists(email);
      
      toast.success(`Created "${listName}" and added photo!`);
      setFavoritedPhotoIds(prev => new Set([...prev, photoId]));
    } catch (error) {
      console.error('Error creating favorites list:', error);
      toast.error('Failed to create favorites list');
      throw error;
    }
  };

  const handleSelectFavoritesList = async (listId, photoId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/public/gallery/${shareToken}/favorites/add`,
        {
          list_id: listId,
          photo_id: photoId
        }
      );

      const list = userFavoritesLists.find(l => l.id === listId);
      toast.success(`Added to "${list?.list_name || 'favorites'}"`);
      setFavoritedPhotoIds(prev => new Set([...prev, photoId]));
      
      // Refresh the lists to update photo counts
      if (favoritesEmail) {
        await loadUserFavoritesLists(favoritesEmail);
      }
    } catch (error) {
      console.error('Error adding to favorites:', error);
      toast.error('Failed to add to favorites');
      throw error;
    }
  };

  const handleLoadFavoritesEmail = async () => {
    const cleanEmail = favoritesEmail.trim().toLowerCase();
    if (!cleanEmail) {
      toast.error('Please enter your email');
      return;
    }

    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/public/gallery/${shareToken}/favorites/lists/${encodeURIComponent(cleanEmail)}`
      );

      if (response.data.length === 0) {
        toast.info('No favorites lists found for this email');
        return;
      }

      setFavoritesEmail(cleanEmail);
      setUserFavoritesLists(response.data);
      sessionStorage.setItem('favoritesUserEmail', cleanEmail);
      setShowFavoritesEmailDialog(false);
      toast.success(`Found ${response.data.length} favorites list(s)!`);
    } catch (error) {
      console.error('Error loading favorites:', error);
      toast.error('Failed to load favorites');
    }
  };


  const handleCreateList = async () => {
    if (!newListName.trim()) {
      toast.error('Please enter a list name');
      return;
    }

    // Get email from state or userInfo
    const emailToUse = favoritesEmail || userEmail || userInfo?.email;
    
    if (!emailToUse) {
      toast.error('Email not found. Please reload the page.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('list_name', newListName.trim());
      formData.append('email', emailToUse);

      const response = await axios.post(
        `${BACKEND_URL}/api/public/gallery/${shareToken}/favorites/create-list`,
        formData
      );
      
      // Save email to session storage
      sessionStorage.setItem('favoritesUserEmail', emailToUse);
      setFavoritesEmail(emailToUse);
      
      toast.success(`List "${newListName}" created!`);
      setNewListName('');
      setShowCreateListDialog(false);
      
      // Reload lists
      await loadUserFavoritesLists(emailToUse);
    } catch (error) {
      console.error('Error creating list:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to create list';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to create list');
    }
  };

  const handleDeleteList = async (listId) => {
    try {
      await axios.delete(
        `${BACKEND_URL}/api/public/gallery/${shareToken}/favorites/list/${listId}`
      );
      
      toast.success('List deleted successfully');
      setListToDelete(null);
      
      // Reload lists
      await loadUserFavoritesLists(favoritesEmail);
      
      // Clear selected list if it was deleted
      if (selectedFavoritesList?.id === listId) {
        setSelectedFavoritesList(null);
        setFavoritesPhotos([]);
      }
    } catch (error) {
      console.error('Error deleting list:', error);
      toast.error('Failed to delete list');
    }
  };


  const handleViewFavoritesList = async (list) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/public/gallery/${shareToken}/favorites/list/${list.id}`
      );
      setFavoritesPhotos(response.data);
      setSelectedFavoritesList(list);
      setActiveTab('favorites');
    } catch (error) {
      console.error('Error loading favorites photos:', error);
      toast.error('Failed to load favorites photos');
    }
  };

  const handleDownloadFavoritesList = (list) => {
    // Paywall check
    if (gallery?.paywall_enabled) {
      setShowPaywallModal(true);
      return;
    }

    // Show download size dialog before starting download
    setPendingDownloadAction({ type: 'favorites', list: list });
    setShowDownloadSizeDialog(true);
  };

  const executeFavoritesListDownload = async (list, size) => {
    try {
      setIsDownloadingAll(true);
      setDownloadRequestSuccess(false);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/public/gallery/${shareToken}/favorites/download-list/${list.id}`,
        { 
          userName: favoritesEmail.split('@')[0],
          userEmail: favoritesEmail,
          size,
          visitorId: getVisitorId()
        }
      );
      
      // Show success state in modal instead of toast
      setDownloadRequestSuccess(true);
      setDownloadUserEmailNotify(favoritesEmail);
      
    } catch (error) {
      console.error('Favorites download request failed:', error);
      setIsDownloadingAll(false);
      setDownloadRequestSuccess(false);
      toast.error('Failed to request download. Please try again.');
    }
  };

  const openLightbox = (photo, index) => {
    setDeepLinkPhoto(null);
    setLightboxPhoto(photo);
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxPhoto(null);
    setDeepLinkPhoto(null);
    // Drop the ?photo= param so closing returns to a clean gallery URL.
    if (new URLSearchParams(window.location.search).get('photo')) {
      navigate(collectionSlug ? `/share/${shareToken}/c/${collectionSlug}` : `/share/${shareToken}`, { replace: true });
    }
  };

  const navigateLightbox = (direction) => {
    if (!displayPhotos || displayPhotos.length === 0) return;

    const newIndex = direction === 'next'
      ? (lightboxIndex + 1) % displayPhotos.length
      : (lightboxIndex - 1 + displayPhotos.length) % displayPhotos.length;

    setLightboxIndex(newIndex);
    setLightboxPhoto(displayPhotos[newIndex]);
  };

  const toggleTag = async (tag) => {
    const newSelectedTags = selectedTags.includes(tag) 
      ? selectedTags.filter(t => t !== tag)
      : [...selectedTags, tag];
    
    setSelectedTags(newSelectedTags);
    
    // Auto-trigger search with the new tags
    if (newSelectedTags.length > 0) {
      try {
        const formData = new FormData();
        formData.append('tags', newSelectedTags.join(','));
        
        if (selectedCategory !== 'all') {
          formData.append('category', selectedCategory);
        }

        const response = await axios.post(
          `${BACKEND_URL}/api/public/gallery/${shareToken}/search/by-tags`,
          formData
        );

        setSearchResults(response.data.photos);
        setActiveTab('tags');
        toast.success(`Found ${response.data.total || response.data.photos.length} matching photos`);
      } catch (error) {
        console.error('Error searching by tags:', error);
        toast.error('Failed to search photos');
      }
    } else {
      setSearchResults([]);
    }
  };

  const handleDownloadAll = async () => {
    // Paywall check
    if (gallery?.paywall_enabled) {
      setShowPaywallModal(true);
      return;
    }

    // Check if user info exists in session
    const storedName = sessionStorage.getItem('downloadUserName');
    const storedEmail = sessionStorage.getItem('downloadUserEmail');
    
    // If no user info, show dialog first
    if (!storedName || !storedEmail) {
      setPendingDownloadPhoto('DOWNLOAD_ALL'); // Special marker for download all
      setShowUserInfoDialog(true);
      return;
    }
    
    // User info exists, show size selection dialog
    setPendingDownloadAction({ type: 'all', photo: null });
    setShowDownloadSizeDialog(true);
  };
  
  const executeDownloadAll = async (userName, userEmail, size = 'full') => {
    try {
      setIsDownloadingAll(true);
      setDownloadRequestSuccess(false);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/public/gallery/${shareToken}/download-all`,
        { userName, userEmail, size, visitorId: getVisitorId() }
      );
      
      // Show success state in modal instead of toast
      setDownloadRequestSuccess(true);
      setDownloadUserEmailNotify(userEmail);
      
    } catch (error) {
      console.error('Download request failed:', error);
      setIsDownloadingAll(false);
      setDownloadRequestSuccess(false);
      toast.error('Failed to request download. Please try again.');
    }
  };
  
  const handleCloseDownloadModal = () => {
    setIsDownloadingAll(false);
    setDownloadRequestSuccess(false);
    setDownloadUserEmailNotify('');
  };

  // Get photos based on active tab with sorting
  const displayPhotos = useMemo(() => {
    // Add null/undefined guards for all arrays
    const safePhotos = photos || [];
    const safeSearchResults = searchResults || [];
    const safeFavoritesPhotos = favoritesPhotos || [];
    const safeFaceSearchResults = faceSearchResults || [];
    
    let basePhotos = activeTab === 'all' ? safePhotos : 
                     activeTab === 'tags' ? safeSearchResults : 
                     activeTab === 'favorites' ? safeFavoritesPhotos :
                     safeFaceSearchResults;
    
    // Ensure basePhotos is always an array
    if (!Array.isArray(basePhotos)) {
      basePhotos = [];
    }
    
    // Apply sorting by image creation date (when photo was taken)
    const sorted = [...basePhotos].sort((a, b) => {
      // Use image_created_at (EXIF date) if available, fallback to uploaded_at or upload_date
      const dateA = new Date(a.image_created_at || a.uploaded_at || a.upload_date || 0).getTime();
      const dateB = new Date(b.image_created_at || b.uploaded_at || b.upload_date || 0).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });
    
    return sorted;
  }, [activeTab, photos, searchResults, favoritesPhotos, faceSearchResults, sortOrder]);

  if (loading) {
    return (
      <div className="min-h-screen bg-tiq-nearblack flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 rounded-full animate-spin mb-4" style={{ border: '3px solid rgba(191,33,47,0.25)', borderTopColor: '#BF212F' }}></div>
          <p className="font-tiq-mono tiq-kicker" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Loading gallery…</p>
        </div>
      </div>
    );
  }

  if (!gallery) {
    return (
      <div className="min-h-screen bg-tiq-nearblack flex items-center justify-center text-white">
        <div className="text-center px-6">
          <h1 className="font-tiq-display uppercase mb-2" style={{ fontWeight: 900, fontSize: 32, letterSpacing: '-0.02em' }}>Gallery not found</h1>
          <p style={{ color: 'rgba(255,255,255,0.55)' }}>This gallery does not exist or has been removed.</p>
        </div>
      </div>
    );
  }

  // Show access gate if user hasn't provided info yet
  if (!hasAccess && gallery) {
    return (
      <div className="min-h-screen bg-tiq-nearblack">
        <Toaster position="top-center" richColors />
        <GalleryAccessGate
          galleryName={gallery.name}
          onAccessGranted={handleAccessGranted}
          open={true}
        />
      </div>
    );
  }

  // Space Mono kicker for hero: terse date · photo count
  const heroKicker = [
    gallery.event_date
      ? new Date(gallery.event_date).toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })
      : null,
    `${gallery.photo_count || 0} Photos`,
  ].filter(Boolean).join(' · ');

  // Resolve featured cover photo URL (absolute or via backend preview route)
  const heroImageUrl = gallery.featured_photo_preview
    ? (gallery.featured_photo_preview.startsWith('http')
        ? gallery.featured_photo_preview
        : `${BACKEND_URL}/api/photos/file/previews/${gallery.featured_photo_preview.split('/').pop()}`)
    : null;

  return (
    <div className="min-h-screen bg-tiq-nearblack font-tiq-body">
      {/* Skip links for keyboard accessibility */}
      <a
        href="#photo-grid"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-white focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:text-slate-900 focus:font-medium focus:outline-none focus:ring-2 focus:ring-[#BF212F]"
      >
        Skip to photos
      </a>

      {/* ARIA Live Region for screen reader announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {loading && 'Loading gallery photos...'}
        {!loading && displayPhotos.length > 0 && `Showing ${displayPhotos.length} photos`}
        {!loading && displayPhotos.length === 0 && 'No photos found'}
      </div>

      <Toaster position="top-center" richColors />
      {savePrompt}
      <WebcamCapture 
        open={showWebcam} 
        onClose={() => setShowWebcam(false)}
        onCapture={handleWebcamCapture}
      />

      <FavoritesListModal
        isOpen={showFavoritesModal}
        onClose={() => {
          setShowFavoritesModal(false);
          setPendingFavoritePhoto(null);
        }}
        userLists={userFavoritesLists}
        onCreateList={handleCreateFavoritesList}
        onSelectList={handleSelectFavoritesList}
        photoId={pendingFavoritePhoto?.id}
        userEmail={userEmail}
      />
      
      {/* Download Progress Modal */}
      <Dialog open={isDownloadingAll} onOpenChange={downloadRequestSuccess ? handleCloseDownloadModal : () => {}}>
        <DialogContent className="sm:max-w-md font-tiq-body text-white border-0" style={{ background: '#141416', borderRadius: 14, border: '1px solid rgba(255,255,255,0.1)' }} onPointerDownOutside={(e) => !downloadRequestSuccess && e.preventDefault()} onEscapeKeyDown={(e) => !downloadRequestSuccess && e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-tiq-display uppercase text-center" style={{ color: '#fff', fontWeight: 800, fontSize: 22, letterSpacing: '-0.015em' }}>
              {downloadRequestSuccess ? 'Request received' : 'Preparing download'}
            </DialogTitle>
          </DialogHeader>

          {!downloadRequestSuccess ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-6">
              <div className="relative">
                <div className="w-20 h-20 rounded-full animate-spin" style={{ border: '4px solid rgba(255,255,255,0.12)', borderTopColor: '#BF212F' }}></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Download className="w-8 h-8" style={{ color: 'rgba(255,255,255,0.4)' }} />
                </div>
              </div>
              <div className="text-center space-y-2 px-4">
                <p className="font-tiq-heading text-lg font-semibold text-white">Submitting your request…</p>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>Please wait a moment</p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 space-y-6">
              <div className="flex items-center justify-center" style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(34,197,94,0.15)' }}>
                <svg className="w-12 h-12" style={{ color: '#22C55E' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="text-center space-y-4 px-6">
                <p className="font-tiq-heading text-lg font-semibold text-white">Download request received!</p>
                <div className="space-y-1" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: 16 }}>
                  <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>We'll email you at</p>
                  <p className="font-tiq-heading" style={{ fontWeight: 700, color: '#fff' }}>{downloadUserEmailNotify}</p>
                  <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>when your photos are ready</p>
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 6 }}>(usually 2–5 minutes)</p>
                </div>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>
                  You can close this window and keep browsing. The download link is valid for 24 hours.
                </p>
              </div>
              <button onClick={handleCloseDownloadModal} className="font-tiq-heading w-full text-white" style={{ background: '#BF212F', borderRadius: 10, padding: '12px 18px', fontWeight: 600, cursor: 'pointer' }}>
                Close
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* QR Code Modal */}
      <QRCodeModal
        open={showQRModal}
        onClose={() => setShowQRModal(false)}
        galleryName={gallery?.name}
      />

      {/* Share Photo Modal */}
      <SharePhotoModal
        open={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setSharePhoto(null);
        }}
        photo={sharePhoto}
        galleryName={gallery?.name}
        shareToken={shareToken}
      />

      {/* Header — Cinema scroll-reveal glass bar (hidden over hero) */}
      <header
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between gap-[14px] font-tiq-body"
        style={{
          padding: '13px clamp(16px,3vw,38px)',
          background: 'rgba(10,10,11,0.5)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          transform: showHeader ? 'none' : 'translateY(-105%)',
          opacity: showHeader ? 1 : 0,
          pointerEvents: showHeader ? 'auto' : 'none',
          transition: 'transform 0.35s var(--tiq-ease), opacity 0.35s var(--tiq-ease)',
        }}
      >
        <img src="/turbo-logo-white.png" alt="Turbo 360" style={{ height: 26, width: 'auto', display: 'block' }} />
        <div className="flex items-center gap-[10px]">
          <span className="font-tiq-mono hidden sm:inline" style={{ fontSize: 11, letterSpacing: '0.16em', color: 'rgba(255,255,255,0.55)', marginRight: 4 }}>
            {(gallery.photo_count || 0)} PHOTOS
          </span>
          <button
            onClick={() => setShowQRModal(true)}
            title="QR code"
            style={{ width: 38, height: 38, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.06)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
          >
            <QrCode className="w-4 h-4" />
          </button>
          {gallery?.finalized && (
            <>
              <Button
                onClick={() => {
                  if (gallery.paywall_enabled) { setShowPaywallModal(true); return; }
                  window.open(gallery.full_size_zip_url, '_blank');
                }}
                disabled={!gallery.full_size_zip_url}
                className="font-tiq-heading text-white border whitespace-nowrap"
                style={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.22)', borderRadius: 8, fontWeight: 600 }}
                data-testid="download-full-size-button"
              >
                {gallery.paywall_enabled ? <Lock className="w-4 h-4 mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                <span className="hidden sm:inline">Full size</span>
                <span className="sm:hidden">Full</span>
              </Button>
              <Button
                onClick={() => {
                  if (gallery.paywall_enabled) { setShowPaywallModal(true); return; }
                  window.open(gallery.social_size_zip_url, '_blank');
                }}
                disabled={!gallery.social_size_zip_url}
                className="font-tiq-heading text-white whitespace-nowrap"
                style={{ backgroundColor: '#BF212F', borderRadius: 8, fontWeight: 600 }}
                data-testid="download-social-size-button"
              >
                {gallery.paywall_enabled ? <Lock className="w-4 h-4 mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                <span className="hidden sm:inline">Download all</span>
                <span className="sm:hidden">Social</span>
              </Button>
            </>
          )}
        </div>
      </header>

      {/* Main Content */}
      {/* Cinematic hero — falls back to red brand gradient when no cover photo */}
      <CinemaHero
        imageUrl={heroImageUrl}
        focalPoint={gallery.featured_photo_focal_point}
        name={gallery.name}
        kicker={heroKicker}
        fallback={!heroImageUrl}
      />

      <main className="w-full" style={{ position: 'relative', zIndex: 2, background: '#0A0A0B', color: '#fff' }}>

        {/* Gallery Info (when no featured photo) — handled by CinemaHero red fallback */}
        {false && (
          <div className="mb-8 relative overflow-hidden rounded-2xl p-6 sm:p-8">
            <div className="absolute inset-0 gradient-mesh-hero dark:gradient-mesh-dark animate-gradient-flow opacity-90" style={{backgroundSize: '200% 200%'}} />
            <div className="absolute inset-0 backdrop-blur-[1px]" />
            <div className="relative z-10 text-white">
              <h2 className="text-fluid-3xl font-display font-bold tracking-tight mb-3 drop-shadow-lg">{gallery.name}</h2>
              <div className="flex flex-wrap items-center gap-3 text-slate-100">
                <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-md px-3 py-1.5 rounded-full">
                  <Calendar className="w-4 h-4" />
                  <span className="font-medium">{new Date(gallery.event_date).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-md px-3 py-1.5 rounded-full">
                  <ImageIcon className="w-4 h-4" />
                  <span className="font-medium">{gallery.photo_count || 0} photos</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Find My Photos — prominent AI face-search bar */}
        <FindMyPhotosBar
          onSelfie={() => { setActiveTab('faces'); setShowWebcam(true); }}
          onUpload={(file) => { setActiveTab('faces'); setFaceSearchFile(file); }}
        />

        {/* Toolbar — collection pills + sort + AI Top 30 */}
        <div
          className="flex items-center justify-between gap-[14px] flex-wrap"
          style={{ padding: 'clamp(18px,2.5vw,24px) clamp(16px,3vw,38px) clamp(14px,2vw,18px)' }}
        >
          {collections.length > 0 ? (
            <CollectionPills
              collections={collections}
              activeSlug={selectedCollection}
              onSelect={(slug) => handleCollectionChange(slug)}
              totalCount={gallery?.photo_count || 0}
              totalLabel="All"
            />
          ) : (
            <div className="flex-1" />
          )}

          <div className="flex items-center gap-[10px] flex-shrink-0">
            <button
              onClick={() => handleSortChange(sortOrder === 'newest' ? 'oldest' : 'newest')}
              className="font-tiq-heading inline-flex items-center gap-[7px]"
              style={{ padding: '9px 13px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.16)', background: 'transparent', color: '#cfcdc9', fontWeight: 600, fontSize: 12.5, cursor: 'pointer' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 5h10M11 9h7M11 13h4M3 17l3 3 3-3M6 4v16" /></svg>
              {sortOrder === 'newest' ? 'Newest' : 'Oldest'}
            </button>
            {aiTop30Enabled && (
              <button
                onClick={toggleAITop30}
                className="font-tiq-heading inline-flex items-center gap-[7px] whitespace-nowrap"
                style={{
                  padding: '9px 13px', borderRadius: 8, fontWeight: 700, fontSize: 12.5, cursor: 'pointer',
                  background: showAITop30Only ? '#BF212F' : 'transparent',
                  color: showAITop30Only ? '#fff' : '#cfcdc9',
                  border: '1px solid ' + (showAITop30Only ? '#BF212F' : 'rgba(255,255,255,0.16)'),
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3l1.8 4.2L18 9l-4.2 1.8L12 15l-1.8-4.2L6 9l4.2-1.8z" /></svg>
                AI Top 30
              </button>
            )}
          </div>
        </div>

        {selectedCollection && (
          <div style={{ padding: '0 clamp(16px,3vw,38px)' }}>
            <button
              onClick={() => copyCollectionLink(selectedCollection)}
              className="inline-flex items-center gap-1.5 font-tiq-heading"
              style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.55)', cursor: 'pointer' }}
            >
              <Share2 className="w-3.5 h-3.5" />
              {copiedCollectionLink ? 'Link copied!' : 'Copy direct link to this collection'}
            </button>
          </div>
        )}

        {/* Secondary view switch — dark segmented control */}
        <div style={{ padding: '0 clamp(16px,3vw,38px)' }}>
          <div className="tiq-scroll flex gap-2 overflow-x-auto" style={{ paddingBottom: 4 }}>
            {[
              { id: 'all', label: 'All photos', icon: null },
              { id: 'tags', label: 'AI tags', icon: <Sparkles className="w-3.5 h-3.5" /> },
              { id: 'faces', label: 'Find my photos', icon: <User className="w-3.5 h-3.5" /> },
              { id: 'favorites', label: 'Favourites', icon: <Heart className="w-3.5 h-3.5" /> },
            ].map((t) => {
              const on = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className="font-tiq-heading inline-flex items-center gap-2 whitespace-nowrap flex-shrink-0"
                  style={{
                    padding: '8px 14px', borderRadius: 999, fontWeight: 600, fontSize: 12.5, cursor: 'pointer',
                    transition: 'all 0.25s var(--tiq-ease)',
                    background: on ? '#BF212F' : 'rgba(255,255,255,0.07)',
                    color: on ? '#fff' : '#cfcdc9',
                    border: '1px solid ' + (on ? '#BF212F' : 'rgba(255,255,255,0.12)'),
                  }}
                >
                  {t.icon}{t.label}
                </button>
              );
            })}
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="px-[clamp(16px,3vw,38px)]">
          <TabsList className="hidden">
            <TabsTrigger value="all">All Photos</TabsTrigger>
            <TabsTrigger value="tags">AI Tag Search</TabsTrigger>
            <TabsTrigger value="faces">Find My Photos</TabsTrigger>
            <TabsTrigger value="favorites">Favorites</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-6">
            {/* All photos view */}
          </TabsContent>

          <TabsContent value="tags" className="mt-4">
            {/* Compact Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#BF212F]" />
                <h3 className="font-tiq-heading font-semibold text-white">Search by AI Tags</h3>
                <span className="font-tiq-mono text-xs px-2 py-0.5 rounded-full" style={{ color: 'rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.07)' }}>
                  {allTags?.length || 0} tags
                </span>
              </div>
              {selectedTags.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-red-600 hover:bg-red-50 h-7"
                  onClick={() => { setSelectedTags([]); setSearchResults([]); }}
                >
                  Clear ({selectedTags.length})
                </Button>
              )}
            </div>

            {/* Category Pills - Compact horizontal scroll */}
            <div className="flex gap-2 overflow-x-auto pb-3 mb-4 scrollbar-hide">
              {[
                { key: 'all', label: 'All', icon: '🔍', color: '#BF212F' },
                { key: 'facial_attributes', label: 'Facial', icon: '😊', color: '#F59E0B' },
                { key: 'emotions', label: 'Emotions', icon: '💫', color: '#EC4899' },
                { key: 'activities', label: 'Activities', icon: '🎯', color: '#F26B38' },
                { key: 'people_groups', label: 'People', icon: '👥', color: '#7C3AED' },
                { key: 'event_scenes', label: 'Scenes', icon: '🏢', color: '#0891B2' },
                { key: 'event_objects', label: 'Objects', icon: '🎤', color: '#10B981' },
                { key: 'atmosphere', label: 'Vibe', icon: '✨', color: '#6366F1' },
              ].map(cat => (
                <button
                  key={cat.key}
                  onClick={() => setSelectedCategory(cat.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                    selectedCategory === cat.key
                      ? 'text-white shadow-md'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  style={selectedCategory === cat.key ? { backgroundColor: cat.color } : {}}
                >
                  <span>{cat.icon}</span>
                  <span>{cat.label}</span>
                  <span className="opacity-70">
                    ({cat.key === 'all' ? allTags?.length : categorizedTags[cat.key]?.length || 0})
                  </span>
                </button>
              ))}
            </div>

            {/* Tags Grid - Compact */}
            <div className="rounded-lg p-3 mb-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
                {(selectedCategory === 'all' ? allTags : categorizedTags[selectedCategory] || []).map(tag => (
                  <button
                    key={tag}
                    onClick={() => toggleTag(tag)}
                    className="px-2.5 py-1 rounded-md text-xs font-medium transition-all"
                    style={selectedTags.includes(tag)
                      ? { background: '#BF212F', color: '#fff' }
                      : { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.75)', border: '1px solid rgba(255,255,255,0.12)' }}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Selected Tags Summary - Compact */}
            {selectedTags.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap bg-red-50 rounded-lg p-2 mb-4">
                <span className="text-xs text-red-700 font-medium">Selected:</span>
                {selectedTags.map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1 bg-white text-red-700 px-2 py-0.5 rounded text-xs border border-red-200">
                    {tag}
                    <button onClick={() => toggleTag(tag)} className="hover:text-red-900">×</button>
                  </span>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="faces" className="mt-4">
            {/* Compact Header */}
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5" style={{ color: '#A78BFA' }} />
              <h3 className="font-tiq-heading font-semibold text-white">Find My Photos</h3>
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>AI facial recognition</span>
            </div>

            {/* Compact Upload Section */}
            <div className="rounded-lg p-4 mb-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Photo Preview (if selected) */}
                {faceSearchFile ? (
                  <div className="flex items-center gap-3 flex-1 bg-white rounded-lg p-2 border border-purple-200">
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                      <img
                        src={URL.createObjectURL(faceSearchFile)}
                        alt="Reference"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{faceSearchFile.name}</p>
                      <button
                        onClick={() => setFaceSearchFile(null)}
                        className="text-xs text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Take Selfie Button */}
                    <button
                      onClick={() => setShowWebcam(true)}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 transition-colors"
                    >
                      <Camera className="w-4 h-4" />
                      Take Selfie
                    </button>

                    {/* Upload Button */}
                    <label className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-pink-600 text-white text-sm font-medium hover:bg-pink-700 transition-colors cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setFaceSearchFile(e.target.files[0])}
                        className="hidden"
                      />
                      <Upload className="w-4 h-4" />
                      Upload Photo
                    </label>
                  </>
                )}

                {/* Search Button */}
                <Button
                  onClick={handleFaceSearch}
                  disabled={!faceSearchFile || faceSearching}
                  className="bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium px-6"
                >
                  {faceSearching ? (
                    <span className="animate-pulse">Searching...</span>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-1.5" />
                      Search
                    </>
                  )}
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="favorites" className="mt-4">
            {/* Compact Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-[#BF212F]" fill="#BF212F" />
                <h3 className="font-tiq-heading font-semibold text-white">My Favourites</h3>
                {favoritesEmail && (
                  <span className="font-tiq-mono text-xs px-2 py-0.5 rounded-full" style={{ color: 'rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.07)' }}>
                    {favoritesEmail}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setShowCreateListDialog(true)}
                  size="sm"
                  className="bg-[#BF212F] hover:bg-[#8B1923] text-white h-8 text-xs"
                >
                  <Heart className="w-3.5 h-3.5 mr-1" fill="white" />
                  New List
                </Button>
                {favoritesEmail && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setUserFavoritesLists([]);
                      setFavoritesEmail('');
                      sessionStorage.removeItem('favoritesUserEmail');
                      setSelectedFavoritesList(null);
                    }}
                    className="text-xs text-slate-500 h-8"
                  >
                    Change Email
                  </Button>
                )}
              </div>
            </div>

            {userFavoritesLists.length === 0 ? (
              /* No Lists - Compact Empty State */
              <div className="rounded-lg p-6 text-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Heart className="w-10 h-10 mx-auto mb-3" style={{ color: 'rgba(191,33,47,0.5)' }} />
                <p className="text-sm mb-3" style={{ color: 'rgba(255,255,255,0.75)' }}>No favourites lists yet</p>
                <p className="text-xs mb-4" style={{ color: 'rgba(255,255,255,0.45)' }}>Tap the heart on any photo to start saving favourites</p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center items-center">
                  <Button
                    onClick={() => setShowCreateListDialog(true)}
                    className="bg-[#BF212F] hover:bg-[#8B1923] text-white text-sm"
                  >
                    Create Your First List
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setFavoritesEmail('');
                      setShowFavoritesEmailDialog(true);
                    }}
                    className="text-sm"
                  >
                    Already have lists? Restore them
                  </Button>
                </div>
              </div>
            ) : (
              /* Lists Display - Compact Grid */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {userFavoritesLists.map((list) => (
                  <div
                    key={list.id}
                    className="rounded-lg p-3 cursor-pointer transition-all"
                    style={selectedFavoritesList?.id === list.id
                      ? { background: 'rgba(191,33,47,0.12)', border: '2px solid #BF212F' }
                      : { background: 'rgba(255,255,255,0.04)', border: '2px solid rgba(255,255,255,0.1)' }}
                    onClick={() => handleViewFavoritesList(list)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Heart
                          className="w-4 h-4 text-[#BF212F]"
                          fill={selectedFavoritesList?.id === list.id ? '#BF212F' : 'none'}
                        />
                        <span className="font-medium text-sm text-white truncate">{list.list_name}</span>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); setListToDelete(list); }}
                        className="p-1 rounded"
                        style={{ color: 'rgba(255,255,255,0.45)' }}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
                        {list.photo_count} photo{list.photo_count !== 1 ? 's' : ''}
                      </span>
                      <Button
                        size="sm"
                        className="h-7 text-xs bg-[#BF212F] hover:bg-[#8B1923] text-white"
                        onClick={(e) => { e.stopPropagation(); handleDownloadFavoritesList(list); }}
                        disabled={list.photo_count === 0}
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Download
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {selectedFavoritesList && favoritesPhotos.length > 0 && (
              <div className="mt-4 p-2 bg-red-50 rounded-lg border border-red-200">
                <span className="text-xs font-medium text-red-700">
                  Showing {favoritesPhotos.length} photos from "{selectedFavoritesList.list_name}"
                </span>
              </div>
            )}
          </TabsContent>

          {/* Face Search Progress - Modern 2026 Inline Progress */}
          {faceSearching && (
            <div className="mb-8 animate-fade-in">
              <FaceSearchProgress
                isSearching={faceSearching}
                currentStage="searching"
                onCancel={() => setFaceSearching(false)}
              />
            </div>
          )}
        </Tabs>

        {/* Photos Grid — Cinema masonry on near-black */}
        <div
          id="photo-grid"
          tabIndex={-1}
          style={{
            padding: '0 clamp(16px,3vw,38px) clamp(40px,6vw,64px)',
            ...(gallery?.paywall_enabled ? { WebkitTouchCallout: 'none', userSelect: 'none' } : {}),
          }}
          onContextMenu={gallery?.paywall_enabled ? (e) => e.preventDefault() : undefined}
        >
        {loading ? (
          <div className="animate-fade-in">
            <PhotoGridSkeleton count={12} viewMode="masonry" />
          </div>
        ) : displayPhotos.length === 0 ? (
          <div className="text-center animate-fade-in" style={{ padding: '80px 20px' }}>
            <p className="font-tiq-mono tiq-kicker" style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
              {activeTab === 'all'
                ? 'No photos in this gallery yet'
                : activeTab === 'tags'
                ? 'No photos found with selected tags'
                : activeTab === 'favorites'
                ? 'No favourites list selected'
                : 'No faces found matching your reference photo'}
            </p>
          </div>
        ) : (
          <MasonryGrid
              photos={displayPhotos}
              onPhotoClick={openLightbox}
              onDownload={handleDownloadPhoto}
              onFavorite={handleFavoriteClick}
              favoritedPhotoIds={favoritedPhotoIds}
              backendUrl={BACKEND_URL}
              shareToken={shareToken}
              onLoadMore={activeTab === 'all' && !showAITop30Only ? loadMorePhotos : undefined}
              hasMore={activeTab === 'all' && !showAITop30Only && hasMore}
              confidenceScores={activeTab === 'faces' ? Object.fromEntries(faceSearchResults.map(r => [r.id, r.confidence || r.similarity])) : {}}
              showConfidence={activeTab === 'faces'}
              paywallEnabled={gallery?.paywall_enabled}
            />
        )}
        </div>

        {/* Share Collection Card - shown after face search results */}
        {activeTab === 'faces' && faceSearchResults.length > 0 && displayPhotos.length > 0 && (
          <div className="mt-8 max-w-lg mx-auto">
            <ShareCollectionCard
              photos={displayPhotos}
              galleryName={gallery?.name || 'Gallery'}
              shareToken={shareToken}
              onDownloadAll={() => {
                displayPhotos.forEach(photo => handleDownloadPhoto(photo));
                toast.success(`Starting download of ${displayPhotos.length} photo${displayPhotos.length > 1 ? 's' : ''}...`);
              }}
            />
          </div>
        )}

        {/* How it works box - shown after results on faces tab */}
        {activeTab === 'faces' && faceSearchResults.length > 0 && (
          <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 max-w-2xl mx-auto">
            <div className="flex items-start gap-3">
              <div className="text-blue-600 dark:text-blue-400 mt-0.5">ℹ️</div>
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-medium mb-1">How it works:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-300">
                  <li>Upload a clear photo showing your face</li>
                  <li>AI will search all photos in this gallery</li>
                  <li>Results show photos with matching faces and similarity scores</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* PhotoLightbox - Modern 2026 UX with animations */}
        <PhotoLightbox
          open={!!lightboxPhoto}
          onClose={closeLightbox}
          photos={deepLinkPhoto ? [deepLinkPhoto] : displayPhotos}
          initialIndex={deepLinkPhoto ? 0 : lightboxIndex}
          onDownload={handleDownloadPhoto}
          onFavorite={handleFavoriteClick}
          onShare={(photo) => {
            setSharePhoto(photo);
            setShowShareModal(true);
          }}
          favoritedPhotoIds={favoritedPhotoIds}
          shareToken={shareToken}
          backendUrl={BACKEND_URL}
          paywallEnabled={gallery?.paywall_enabled}
        />

        {/* Footer — tri-colour signal bar */}
        <CinemaFooter />

      </main>

      {/* User Info Dialog */}
      <Dialog open={showUserInfoDialog} onOpenChange={setShowUserInfoDialog}>
        <DialogContent className="max-w-md font-tiq-body text-white border-0" style={{ background: '#141416', borderRadius: 14, border: '1px solid rgba(255,255,255,0.1)' }}>
          <DialogHeader>
            <DialogTitle className="font-tiq-display uppercase" style={{ color: '#fff', fontWeight: 800, letterSpacing: '-0.015em' }}>Download photo</DialogTitle>
            <DialogDescription style={{ color: 'rgba(255,255,255,0.55)' }}>
              Please provide your details to download photos from this gallery
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="download-name" className="font-tiq-heading" style={{ color: 'rgba(255,255,255,0.75)' }}>Your name</Label>
              <input
                id="download-name" type="text" placeholder="Jane Smith" value={userName}
                onChange={(e) => setUserName(e.target.value)} data-testid="download-name-input"
                className="w-full px-3 py-2.5 rounded-lg focus:outline-none"
                style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.14)', color: '#fff' }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="download-email" className="font-tiq-heading" style={{ color: 'rgba(255,255,255,0.75)' }}>Your email</Label>
              <input
                id="download-email" type="email" placeholder="jane@example.com" value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)} data-testid="download-email-input"
                className="w-full px-3 py-2.5 rounded-lg focus:outline-none"
                style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.14)', color: '#fff' }}
              />
            </div>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
              Your information is shared with the gallery owner for download tracking only.
            </p>
          </div>
          <DialogFooter>
            <button
              onClick={() => { setShowUserInfoDialog(false); setPendingDownloadPhoto(null); }}
              className="font-tiq-heading" style={{ border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)', borderRadius: 10, padding: '10px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer', background: 'transparent' }}
            >
              Cancel
            </button>
            <button
              onClick={handleUserInfoSubmit} data-testid="submit-user-info"
              className="font-tiq-heading text-white" style={{ backgroundColor: '#BF212F', borderRadius: 10, padding: '10px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
            >
              Continue download
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Download Size Selection Dialog */}
      <DownloadSizeDialog
        isOpen={showDownloadSizeDialog}
        onClose={() => {
          if (!isPreparingDownload) {
            setShowDownloadSizeDialog(false);
            setPendingDownloadAction(null);
          }
        }}
        onSelectSize={handleDownloadSizeSelection}
        photoCount={
          pendingDownloadAction?.type === 'all' ? photos.length :
          pendingDownloadAction?.type === 'favorites' ? pendingDownloadAction?.list?.photo_count :
          1
        }
        isLoading={isPreparingDownload}
      />

      {/* Paywall Modal */}
      <Dialog open={showPaywallModal} onOpenChange={setShowPaywallModal}>
        <DialogContent className="sm:max-w-md [&>button]:hidden font-tiq-body text-white border-0" style={{ background: '#141416', borderRadius: 14, border: '1px solid rgba(255,255,255,0.1)' }}>
          <DialogHeader>
            <div className="flex items-center justify-center mx-auto mb-3" style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(191,33,47,0.15)' }}>
              <Lock className="w-7 h-7 text-[#BF212F]" />
            </div>
            <DialogTitle className="font-tiq-display uppercase text-center" style={{ color: '#fff', fontWeight: 900, fontSize: 24, letterSpacing: '-0.02em' }}>Gallery locked</DialogTitle>
            <DialogDescription className="text-center mt-2" style={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>
              Due to accepted contract terms, downloading this imagery isn't available until payment has been received.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0 mt-4 sm:flex-col">
            {gallery?.paywall_url && (
              <button onClick={() => window.open(gallery.paywall_url, '_blank')} className="font-tiq-heading w-full text-white" style={{ backgroundColor: '#BF212F', borderRadius: 10, padding: '12px 18px', fontWeight: 600, cursor: 'pointer' }}>
                Unlock downloads
              </button>
            )}
            {gallery?.contract_url && (
              <button onClick={() => window.open(gallery.contract_url, '_blank')} className="font-tiq-heading w-full" style={{ border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.75)', borderRadius: 10, padding: '12px 18px', fontWeight: 600, cursor: 'pointer', background: 'transparent' }}>
                View contract
              </button>
            )}
            <button onClick={() => setShowPaywallModal(false)} className="font-tiq-heading w-full" style={{ color: 'rgba(255,255,255,0.55)', fontWeight: 600, fontSize: 13, padding: '8px', cursor: 'pointer', background: 'transparent', border: 'none' }}>
              Keep browsing
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Find My Photos Modal - No Matches Found */}
      <Dialog open={showFindMyPhotosModal} onOpenChange={setShowFindMyPhotosModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{color: '#BF212F'}}>
              No Matches Found
            </DialogTitle>
            <DialogDescription className="text-base mt-2">
              We didn't find any photos matching your face in this gallery yet. 
              Would you like to be notified when new photos matching your face are uploaded?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="notify-email">Email Address</Label>
              <Input
                id="notify-email"
                type="email"
                value={notifyEmail}
                disabled
                className="w-full bg-slate-50"
              />
              <p className="text-sm text-slate-500">
                We'll send you an email (max once per hour) when matching photos are uploaded.
              </p>
            </div>
            
            <div className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <input
                type="checkbox"
                id="notify-consent"
                checked={notifyConsent}
                onChange={(e) => setNotifyConsent(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300 focus:ring-2 focus:ring-offset-0"
                style={{accentColor: '#BF212F'}}
              />
              <label htmlFor="notify-consent" className="text-sm text-slate-700 cursor-pointer">
                I consent to AI facial matching to help identify and group my photos within the gallery
              </label>
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowFindMyPhotosModal(false);
                setNotifyEmail('');
                setNotifyConsent(false);
              }}
              disabled={submittingSubscription}
            >
              Not Now
            </Button>
            <Button
              onClick={handleSubscribeToNotifications}
              disabled={submittingSubscription}
              className="text-white"
              style={{backgroundColor: '#BF212F'}}
            >
              {submittingSubscription ? 'Subscribing...' : 'Notify Me'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create List Dialog */}
      <Dialog open={showCreateListDialog} onOpenChange={setShowCreateListDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-[#BF212F]" fill="#BF212F" />
              Create New Favorites List
            </DialogTitle>
            <DialogDescription>
              Create a new list to organize your favorite photos from this gallery.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="list-name">List Name</Label>
              <Input
                id="list-name"
                placeholder="e.g., Best Moments, Group Photos"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateList();
                  }
                }}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateListDialog(false);
                setNewListName('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateList}
              className="bg-[#BF212F] hover:bg-[#8B1923]"
            >
              Create List
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Favorites - Email Dialog */}
      <Dialog open={showFavoritesEmailDialog} onOpenChange={setShowFavoritesEmailDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-[#BF212F]" fill="#BF212F" />
              Restore Your Favorites
            </DialogTitle>
            <DialogDescription>
              Enter the email address you used when creating your favorites lists to load them back.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="restore-email">Email Address</Label>
              <Input
                id="restore-email"
                type="email"
                placeholder="you@example.com"
                value={favoritesEmail}
                onChange={(e) => setFavoritesEmail(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleLoadFavoritesEmail();
                  }
                }}
                autoFocus
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowFavoritesEmailDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleLoadFavoritesEmail}
              className="bg-[#BF212F] hover:bg-[#8B1923]"
            >
              Restore Lists
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete List Confirmation Dialog */}
      <Dialog open={!!listToDelete} onOpenChange={() => setListToDelete(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete List?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{listToDelete?.list_name}"? This will remove the list and all photo associations. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> The photos will not be deleted from the gallery, only removed from this list.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setListToDelete(null)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDeleteList(listToDelete.id)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete List
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 mt-12 mb-16 sm:mb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center text-slate-600 dark:text-slate-400">
          <p>Powered by Turbo IQ • AI for Every Shot, Every Face, Every Brand.</p>
        </div>
      </footer>

      {/* Mobile Bottom Navigation - hidden until scroll past hero */}
      <MobileBottomNav
        activeTab={activeTab}
        onTabChange={setActiveTab}
        favoritesCount={favoritedPhotoIds.size}
        visible={showHeader}
      />
    </div>
  );
};


export default PublicGallery;
